/**
 * Defence 1 on the screen: what Wend can tell about this list **without sending
 * anything to it**, before anybody presses Schedule.
 *
 * ## The four words that are the most important thing on this panel
 *
 * `listHealthUnknowableNote(channel)` — *"Cannot detect dead mailboxes."* on
 * email, *"Cannot detect numbers not on WhatsApp."* on the other — is rendered
 * unfolded, in the same block as the verdict, on every branch including the
 * green one. It is the second kind of text in the Shipping Reach rule:
 * honesty about a number, which stays visible and gets four words rather than a
 * paragraph. Everything else the check cannot do is argued in `list-safety.ts`'s
 * header and is deliberately not on this screen.
 *
 * It is read **from the wire** (`health.unknowable`) rather than recomputed,
 * because the contract puts it in the payload precisely so a screen cannot
 * forget it. The accessor is the fallback for the one case the wire can produce
 * and the reader must never see: an empty string. A missing caveat is the
 * failure mode this panel exists to prevent, so it fails to the caveat rather
 * than to silence.
 *
 * ## EVERY SENTENCE ON THIS PANEL IS CHOSEN BY THE CAMPAIGN'S OWN CHANNEL
 *
 * The verdict copy used to be keyed on the verdict alone, and two of its four
 * details are email's: a WhatsApp campaign whose list is `high_risk` was told
 * that bounces count against its Amazon account and could get that account
 * reviewed. `listHealthVerdictCopy` and `listHealthReasonTitle` both take a
 * channel now, and the channel comes from `health.signals.channel` — the same
 * report the counts came out of — rather than from a prop, so the words and the
 * numbers on this panel can never describe two different channels.
 *
 * ## Why a green verdict still shows the note and still gets a plan
 *
 * `ready` means "nothing in the data argues against sending". It does not mean
 * the addresses exist — an offline check reads spelling, suppressions, past
 * bounces, duplicates and domain spread, and none of those knows whether a
 * mailbox is live. That is the whole reason defence 2 is not a branch of
 * defence 1, and it is why this panel never renders a tick that could be read as
 * a clearance.
 *
 * ## What is folded and what is not
 *
 * `summary` is on screen; `detail` is behind `MoreDetail`. That is the third
 * kind of text — instruction, short by default with detail on demand — and slice
 * 16's recorded mistake was applying that rule backwards and deleting the
 * instruction entirely. Nothing a person would draw a **false conclusion**
 * without is behind the fold: not the caveat, not a single finding sentence, not
 * the verdict.
 *
 * `MoreDetail` is imported across a route boundary from `routes/templates`,
 * which its own header anticipates and names as the cost of not having moved it
 * to `components/` yet. `NewCampaignPage` already reaches into
 * `routes/integrations` for `AdvancedDisclosure` on the same argument: a second
 * `<details>` recipe on a third route is the accretion slice 16 spent itself
 * undoing. Moving either file is a rename under other agents' feet and is
 * recorded rather than done.
 *
 * ## The findings are the API's sentences, and the headings are this file's
 *
 * Each finding carries a whole sentence with this list's real counts in it,
 * written by `listHealth` and printed unchanged. `LIST_HEALTH_REASON_TITLE`
 * supplies only which of the eight it is, at a glance — `CAMPAIGN_ISSUE_TITLE`'s
 * rule, headings and never repairs, because two accounts of one problem drift
 * and only one of them gets edited.
 *
 * Severe findings lead, because the verdict is a count of the severe ones: a
 * reader who stops after the first line has read the one that produced the band.
 */
import {
  listHealthUnknowableNote,
  listHealthVerdictCopy,
  type ListHealthReport,
} from '@wend/shared';
import { Badge, Callout, Kicker } from '@wend/ui';

import {
  listHealthReasonTitle,
  LIST_HEALTH_SEVERITY_TONE,
  LIST_HEALTH_VERDICT_TONE,
  sortFindings,
} from '../../lib/list-safety-display';
import { MoreDetail } from '../templates/MoreDetail';

export interface ListHealthPanelProps {
  health: ListHealthReport;
}

export function ListHealthPanel({ health }: ListHealthPanelProps) {
  // The channel comes off the report itself, never off a prop: the words and
  // the counts on this panel are then guaranteed to be about one channel.
  const channel = health.signals.channel;
  const copy = listHealthVerdictCopy(channel, health.verdict);
  const findings = sortFindings(health.findings);

  // The wire carries it so the screen cannot forget it; the constant is what
  // stands in if the wire ever sends nothing. See the header.
  const unknowable =
    health.unknowable.trim() === '' ? listHealthUnknowableNote(channel) : health.unknowable;

  return (
    <section className="flex flex-col gap-4" aria-label="List check">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Kicker as="span">
            This list
          </Kicker>
          <Badge tone={LIST_HEALTH_VERDICT_TONE[health.verdict]}>{copy.label}</Badge>
        </div>

        {/*
          SLICE 28 REMOVED THREE THINGS FROM THIS HEADER, AND THE OWNER QUOTED
          ALL THREE.

          `copy.summary` was *"A few things are worth knowing before this goes
          out."* It is a preamble to a list that sits directly beneath it,
          carrying no count, no fact and no action — and the badge beside the
          title already gives the verdict in one word.

          `unknowable` was *"Cannot detect dead mailboxes."* It is a disclaimer
          about the checking method, not about this list. A sender reading
          "Looks fine / Cannot detect dead mailboxes" learns only that the
          product distrusts itself.

          `copy.detail`, behind *"What Wend checked, and what it could not"*, is
          methodology — and on the `high_risk` and `do_not_send` verdicts it
          explains AWS account review, which is an operator's subject. Folding it
          never made it sender content; it only hid the word count while leaving
          the summary line on screen permanently.

          What is left is the verdict badge and the findings themselves, which
          are the part with counts in them. Both constants stay in the contract
          for the operator screen that will carry them.
        */}
      </div>

      {findings.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {findings.map((finding) => (
            <li key={finding.code}>
              <Callout
                tone={LIST_HEALTH_SEVERITY_TONE[finding.severity]}
                title={listHealthReasonTitle(channel, finding.code)}
              >
                {finding.message}
              </Callout>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
